
import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { Game } from '@/types/game';
import { createGameUrl } from '@/utils/gameUtils';

export const SearchBar = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { data: searchResults, isLoading: isSearchLoading } = useQuery({
    queryKey: ['search-games', debouncedSearchQuery],
    queryFn: async () => {
      if (!debouncedSearchQuery) {
        return [];
      }
      const { data, error } = await supabase
        .from('games')
        .select('id, title, thumbnail, category')
        .ilike('title', `${debouncedSearchQuery}%`)
        .eq('hidden', false)
        .limit(5);
      
      if (error) throw error;
      return data;
    },
    enabled: debouncedSearchQuery.length > 0,
  });

  useOnClickOutside(searchContainerRef, () => setIsSearchFocused(false));
  
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsSearchFocused(false);
    }
  };

  return (
    <div className="flex items-center space-x-4">
      <form onSubmit={handleSearchSubmit} ref={searchContainerRef} className="relative group">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 w-4 h-4 transition-colors duration-300" />
          <Input
            type="text"
            placeholder="Search games..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            className="pl-10 w-72 bg-gray-50/50 border-gray-200/50 focus:border-blue-500/50 focus:bg-white rounded-xl shadow-sm focus:shadow-md transition-all duration-300 backdrop-blur-sm"
          />
          
          {/* Search input glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
        </div>

        {isSearchFocused && debouncedSearchQuery.length > 0 && (
          <div className="absolute top-full mt-3 w-full bg-white/95 backdrop-blur-xl rounded-xl shadow-xl border border-gray-100/50 z-50 overflow-hidden">
            {/* Top gradient accent */}
            <div className="h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"></div>
            
            {isSearchLoading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="inline-flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                  <span>Loading...</span>
                </div>
              </div>
            ) : searchResults && searchResults.length > 0 ? (
              <ul className="max-h-96 overflow-y-auto">
                {searchResults.map((game: Partial<Game>) => (
                  <li key={game.id}>
                    <Link 
                      to={createGameUrl(game.title!)}
                      className="flex items-center p-3 hover:bg-blue-50/50 transition-colors duration-200 group/item"
                      onClick={() => {
                        setSearchQuery('');
                        setIsSearchFocused(false);
                      }}
                    >
                      <div className="flex-shrink-0 mr-3">
                        <img 
                          src={game.thumbnail} 
                          alt={game.title} 
                          className="w-12 h-12 object-cover rounded-lg shadow-sm group-hover/item:shadow-md transition-shadow duration-200" 
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 group-hover/item:text-blue-600 transition-colors duration-200 truncate">{game.title}</div>
                        <div className="text-sm text-gray-500 capitalize">{game.category}</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-4 text-center text-gray-500">
                <div className="text-gray-400 mb-1">🔍</div>
                No results found.
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );
};
